import { recordIds, type ReminderType, type TenantSettings } from '@dairy/shared';
import {
  breedingStartDue,
  calvingReminderDue,
  dryOffDue,
  expectedCalving,
  heatWatchWindow,
  heiferBreedingDue,
  pregCheckDue,
} from './breeding';
import { eventsOf, isAfter } from './events';
import { nextProtocolDue, protocolApplies, protocolReminderId, protocolReminderType } from './protocols';
import { deriveReproStatus } from './repro';
import { deriveStage } from './stage';
import type { DomainAnimal, DomainEvent, DomainProtocol, DomainReminder } from './types';

export interface DesiredReminder {
  id: string;
  animalId: string;
  type: ReminderType;
  dueDate: string;
  windowEnd: string | null;
  sourceEventId: string | null;
  protocolId: string | null;
  state: 'OPEN' | 'RESOLVED';
  /** Note written when the system closes it (e.g. NO_HEAT_SEEN). */
  note?: string;
}

type Settings = Pick<
  TenantSettings,
  | 'calfMaxAgeMonths'
  | 'gestationDays'
  | 'dryPeriodDays'
  | 'heatWatchStartDays'
  | 'heatWatchEndDays'
  | 'pregCheckDays'
  | 'breedingStartDays'
  | 'heiferBreedingAgeMonths'
>;

const isPregnantCheck = (e: DomainEvent) =>
  e.type === 'PREG_CHECK' && (e.data as { result?: string }).result === 'PREGNANT';

/**
 * D-5: desired reminders for one animal, from its events, the farm's protocols and settings.
 * Exited (or deleted) animals have none, so their open reminders are removed by reconciliation.
 */
export function computeReminders(
  animal: DomainAnimal,
  events: readonly DomainEvent[],
  protocols: readonly DomainProtocol[],
  s: Settings,
  today: string,
): DesiredReminder[] {
  if (animal.deletedAt || animal.status !== 'ACTIVE') return [];
  const evs = eventsOf(events, animal.id);
  const out: DesiredReminder[] = [];
  const base = { animalId: animal.id, windowEnd: null, protocolId: null };
  const stageToday = deriveStage(animal, evs, today, s);

  if (animal.sex === 'FEMALE') {
    const services = evs.filter((e) => e.type === 'SERVICE');
    const calvings = evs.filter((e) => e.type === 'CALVING');
    const repro = deriveReproStatus(animal, evs, today, s);

    for (const S of services) {
      const after = evs.filter((e) => isAfter(e, S));

      // HEAT_WATCH — closed by a HEAT, PREG_CHECK or newer SERVICE (or a CALVING) after it, or when the window has passed.
      const win = heatWatchWindow(S.date, s);
      const hwClosed = after.some((e) => ['HEAT', 'PREG_CHECK', 'SERVICE', 'CALVING'].includes(e.type));
      const expired = today > win.end;
      out.push({
        ...base,
        id: recordIds.eventReminder(animal.id, 'HEAT_WATCH', S.id),
        type: 'HEAT_WATCH',
        dueDate: win.start,
        windowEnd: win.end,
        sourceEventId: S.id,
        state: hwClosed || expired ? 'RESOLVED' : 'OPEN',
        ...(!hwClosed && expired ? { note: 'NO_HEAT_SEEN' } : {}),
      });

      // PREG_CHECK_DUE — closed by a PREG_CHECK, HEAT, ABORTION or newer SERVICE (or a CALVING) after it.
      const pcClosed = after.some((e) => ['PREG_CHECK', 'HEAT', 'ABORTION', 'SERVICE', 'CALVING'].includes(e.type));
      out.push({
        ...base,
        id: recordIds.eventReminder(animal.id, 'PREG_CHECK_DUE', S.id),
        type: 'PREG_CHECK_DUE',
        dueDate: pregCheckDue(S.date, s),
        sourceEventId: S.id,
        state: pcClosed ? 'RESOLVED' : 'OPEN',
      });

      // The service's cycle: events after it, until the next SERVICE or CALVING.
      const cycle: DomainEvent[] = [];
      for (const e of after) {
        if (e.type === 'SERVICE' || e.type === 'CALVING') break;
        cycle.push(e);
      }

      // RESERVE_DUE — source: the first PREG_CHECK (OPEN), HEAT (before any PREGNANT check) or ABORTION.
      let confirmed = false;
      let trigger: DomainEvent | undefined;
      for (const e of cycle) {
        if (isPregnantCheck(e)) confirmed = true;
        else if (e.type === 'PREG_CHECK') {
          trigger = e;
          break;
        } else if (e.type === 'HEAT' && !confirmed) {
          trigger = e;
          break;
        } else if (e.type === 'ABORTION') {
          trigger = e;
          break;
        }
      }
      const checks = cycle.filter((e) => e.type === 'PREG_CHECK');
      const lastCheck = checks[checks.length - 1];
      const cyclePregnant = !cycle.some((e) => e.type === 'ABORTION') && !!lastCheck && isPregnantCheck(lastCheck);
      if (trigger && !cyclePregnant) {
        const t = trigger;
        const reserved = evs.some((e) => e.type === 'SERVICE' && isAfter(e, t));
        out.push({
          ...base,
          id: recordIds.eventReminder(animal.id, 'RESERVE_DUE', t.id),
          type: 'RESERVE_DUE',
          dueDate: t.date,
          sourceEventId: t.id,
          state: reserved ? 'RESOLVED' : 'OPEN',
        });
      }

      // DRY_OFF_DUE / CALVING_DUE — from the conceiving service, once a PREGNANT check confirmed it.
      const pregCheck = cycle.find(isPregnantCheck);
      if (pregCheck) {
        const exp = expectedCalving(S.date, s);
        const pregnantNow = repro?.status === 'PREGNANT' && repro.conceivingServiceId === S.id;
        out.push({
          ...base,
          id: recordIds.eventReminder(animal.id, 'CALVING_DUE', S.id),
          type: 'CALVING_DUE',
          dueDate: calvingReminderDue(exp),
          sourceEventId: S.id,
          state: pregnantNow ? 'OPEN' : 'RESOLVED',
        });
        if (deriveStage(animal, evs, pregCheck.date, s) === 'MILKING') {
          const driedOff = evs.some((e) => e.type === 'DRY_OFF' && isAfter(e, pregCheck));
          out.push({
            ...base,
            id: recordIds.eventReminder(animal.id, 'DRY_OFF_DUE', S.id),
            type: 'DRY_OFF_DUE',
            dueDate: dryOffDue(exp, s),
            sourceEventId: S.id,
            state: pregnantNow && !driedOff && stageToday === 'MILKING' ? 'OPEN' : 'RESOLVED',
          });
        }
      }
    }

    // BREEDING_START — every calving (opening included); closed by the first SERVICE after it (or a later CALVING).
    for (const C of calvings) {
      const resolved = evs.some((e) => (e.type === 'SERVICE' || e.type === 'CALVING') && isAfter(e, C));
      out.push({
        ...base,
        id: recordIds.eventReminder(animal.id, 'BREEDING_START', C.id),
        type: 'BREEDING_START',
        dueDate: breedingStartDue(C.date, s),
        sourceEventId: C.id,
        state: resolved ? 'RESOLVED' : 'OPEN',
      });
    }

    // HEIFER_BREEDING — a female that never calved and was never served; closed by her first SERVICE.
    const firstService = services[0];
    const firstCalving = calvings[0];
    const calvedFirst =
      animal.openingParity > 0 || (!!firstCalving && (!firstService || isAfter(firstService, firstCalving)));
    if (!calvedFirst) {
      out.push({
        ...base,
        id: recordIds.heiferReminder(animal.id),
        type: 'HEIFER_BREEDING',
        dueDate: heiferBreedingDue(animal.birthDate, s),
        sourceEventId: null,
        state: firstService ? 'RESOLVED' : 'OPEN',
      });
    }
  }

  // VACCINATION_DUE / DEWORMING_DUE
  for (const p of protocols) {
    if (!protocolApplies(p, animal, stageToday, today, s)) continue;
    const type = protocolReminderType(p);
    const chain = nextProtocolDue(p, animal, evs, today, s);
    for (const due of chain.resolved) {
      out.push({
        ...base,
        id: protocolReminderId(animal.id, p, due),
        type,
        dueDate: due,
        sourceEventId: null,
        protocolId: p.id,
        state: 'RESOLVED',
      });
    }
    out.push({
      ...base,
      id: protocolReminderId(animal.id, p, chain.dueDate),
      type,
      dueDate: chain.dueDate,
      sourceEventId: null,
      protocolId: p.id,
      state: 'OPEN',
    });
  }

  // One entry per id (the last wins).
  const byId = new Map<string, DesiredReminder>();
  for (const r of out) byId.set(r.id, r);
  return [...byId.values()];
}

export interface ReminderChanges {
  create: DesiredReminder[];
  update: Array<{ id: string; dueDate: string; windowEnd: string | null }>;
  close: Array<{ id: string; note?: string }>;
  remove: string[];
}

/**
 * D-5 reconciliation. Returns only real changes; DONE and DISMISSED reminders are never touched.
 * Running it again on the result returns no changes.
 */
export function reconcileReminders(
  desired: readonly DesiredReminder[],
  stored: readonly DomainReminder[],
): ReminderChanges {
  const storedById = new Map(stored.filter((r) => !r.deletedAt).map((r) => [r.id, r]));
  const desiredById = new Map(desired.map((d) => [d.id, d]));
  const changes: ReminderChanges = { create: [], update: [], close: [], remove: [] };
  for (const d of desiredById.values()) {
    const st = storedById.get(d.id);
    if (d.state === 'OPEN') {
      if (!st) changes.create.push(d);
      else if (
        st.status === 'OPEN' &&
        (st.dueDate !== d.dueDate || (st.windowEnd ?? null) !== (d.windowEnd ?? null))
      ) {
        changes.update.push({ id: d.id, dueDate: d.dueDate, windowEnd: d.windowEnd });
      }
    } else if (st && st.status === 'OPEN') {
      changes.close.push(d.note ? { id: d.id, note: d.note } : { id: d.id });
    }
  }
  for (const st of storedById.values()) {
    if (st.status === 'OPEN' && !desiredById.has(st.id)) changes.remove.push(st.id);
  }
  return changes;
}

export function hasReminderChanges(c: ReminderChanges): boolean {
  return c.create.length + c.update.length + c.close.length + c.remove.length > 0;
}
